import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Check, CreditCard, DoorOpen, FileText, LogOut, PhoneCall, PhoneIncoming, PhoneOff, RefreshCw, Shield, Video, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { useAuth } from "../../context/AuthContext";

// The installed Android app already has a full native incoming-call flow
// (SocietyIncomingCallService -> Telecom -> SocietyIncomingCallActivity,
// wired up via FCM). This page also polls /calls/active every 4s and used to
// pop its own full-screen call UI + ringtone on top of that unconditionally -
// harmless when this page runs in a plain browser (no native flow exists
// there, so this IS the only incoming-call mechanism), but on the native app
// it meant every call showed two different call screens stacked on each
// other once the native side was actually working. window.RpSocietyAndroid
// only exists inside the native WebView, so it's a reliable way to tell them
// apart.
const isNativeApp = typeof window !== "undefined" && Boolean(window.RpSocietyAndroid);
const WEB_CALL_DISABLED_MESSAGE = "Video calls work only in the RP Society Android app. Please use the native mobile app.";

function Pill({ children, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    green: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
  };
  return <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${tones[tone] || tones.slate}`}>{children}</span>;
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4 text-slate-500" />
        <h2 className="text-base font-black text-slate-950">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function IncomingCallScreen({ call, owner, onAnswer, onDecline }) {
  if (!call) return null;
  const caller = call.watchmanName || call.targetStaffName || "Guard";
  const flat = `${call.wing || owner?.wing || ""} ${call.flatNumber || owner?.flatNumber || ""}`.trim();
  return (
    <div className="fixed inset-0 z-[100] flex min-h-screen flex-col justify-between bg-[#07111f] px-6 py-8 text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,#1e3a8a_0%,#07111f_45%,#020617_100%)]" />
      <div className="relative flex items-center justify-between">
        <div>
          <div className="text-[11px] font-black uppercase tracking-[0.28em] text-white/55">RP Society Call</div>
          <div className="mt-2 text-sm font-semibold text-white/70">Incoming video call</div>
        </div>
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10">
          <Video className="h-5 w-5" />
        </div>
      </div>
      <div className="relative flex flex-1 flex-col items-center justify-center text-center">
        <div className="flex h-32 w-32 items-center justify-center rounded-full bg-white/10 text-5xl font-black shadow-2xl ring-8 ring-white/5">
          {caller.slice(0, 1).toUpperCase()}
        </div>
        <div className="mt-8 text-4xl font-black leading-tight">{caller}</div>
        <div className="mt-3 text-lg font-semibold text-white/70">{flat ? `Flat ${flat}` : "Society video call"}</div>
        <div className="mt-7 rounded-full bg-white/10 px-5 py-2 text-sm font-bold text-white/80">Ringing...</div>
      </div>
      <div className="relative mx-auto grid w-full max-w-sm grid-cols-2 gap-10 pb-4">
        <button type="button" onClick={onDecline} className="flex flex-col items-center gap-3 text-sm font-black text-white">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-rose-600 shadow-2xl shadow-rose-950/50">
            <PhoneOff className="h-8 w-8" />
          </span>
          Decline
        </button>
        <button type="button" onClick={onAnswer} className="flex flex-col items-center gap-3 text-sm font-black text-white">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500 shadow-2xl shadow-emerald-950/50">
            <PhoneIncoming className="h-8 w-8" />
          </span>
          Answer
        </button>
      </div>
    </div>
  );
}

export default function SocietyMobileApp() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("home");
  const [callContacts, setCallContacts] = useState([]);
  const [activeCalls, setActiveCalls] = useState([]);
  const [busy, setBusy] = useState(false);
  const [payment, setPayment] = useState({ billId: "", amount: "", utr: "", remarks: "" });
  const [savingContact, setSavingContact] = useState(false);
  const [mutedUntil, setMutedUntil] = useState("");
  const [mutingCalls, setMutingCalls] = useState(false);
  const deviceTokenRef = useRef("");
  const lastIncomingCallToastRef = useRef("");
  const lastLocalCallNoticeRef = useRef("");

  useEffect(() => {
    if (user?.role === "society_staff" && user?.societyRole === "security") {
      navigate("/society/guard", { replace: true });
    }
  }, [navigate, user?.role, user?.societyRole]);

  const load = useCallback(async () => {
    try {
      const [homeRes, contactsRes, callsRes] = await Promise.all([
        api.get("/society/mobile/home"),
        api.get("/society/mobile/call-contacts").catch(() => ({ data: [] })),
        api.get("/society/mobile/calls/active").catch(() => ({ data: [] })),
      ]);
      setData(homeRes.data || {});
      setCallContacts(Array.isArray(contactsRes.data) ? contactsRes.data : []);
      setActiveCalls(Array.isArray(callsRes.data) ? callsRes.data : []);
    } catch (err) {
      toast.error(formatErr(err, "Could not load society app"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const permissions = useMemo(() => new Set(data?.permissions || []), [data]);
  const bills = data?.bills || [];
  const visitors = data?.visitors || data?.pendingVisitors || [];
  const notifications = data?.notifications || [];
  const openBills = bills.filter((bill) => Number(bill.balanceDue || 0) > 0);
  const pendingVisitors = visitors.filter((visitor) => visitor.status === "pending");
  const selectedBill = bills.find((bill) => bill.id === payment.billId);
  // Only "ringing" calls should trigger the full-screen incoming-call UI.
  // activeCalls also includes "connected" calls - e.g. already answered on
  // this resident's other device, since multiple phones can be logged into
  // the same owner account - and treating those as incoming too meant this
  // device kept ringing/showing Answer-Decline for a call someone else had
  // already picked up.
  const incomingCalls = useMemo(() => (
    data?.role === "society_member"
      ? activeCalls.filter((call) => call.initiatorRole === "watchman" && (call.status || "ringing") === "ringing")
      : []
  ), [activeCalls, data?.role]);

  useEffect(() => {
    if (data?.role !== "society_member") return undefined;
    const timer = window.setInterval(() => {
      load();
    }, 4000);
    return () => window.clearInterval(timer);
  }, [data?.role, load]);

  useEffect(() => {
    if (!user?.id || !["society_member", "society_staff"].includes(user?.role)) return undefined;
    let cancelled = false;
    const listeners = [];
    const setupPush = async () => {
      try {
        const { PushNotifications } = await import("@capacitor/push-notifications");
        const permission = await PushNotifications.requestPermissions();
        if (permission.receive !== "granted") return;
        listeners.push(await PushNotifications.addListener("registration", (token) => {
          if (!token?.value) return;
          deviceTokenRef.current = token.value;
          api.post("/society/mobile/push-token", { token: token.value, platform: "android", enabled: true }).catch(() => {});
        }));
        listeners.push(await PushNotifications.addListener("pushNotificationActionPerformed", (event) => {
          const route = event?.notification?.data?.route;
          const callId = event?.notification?.data?.callId;
          if (route?.includes("/society/guard-call/") || route?.includes("/society/resident-call/") || callId) {
            toast.info(WEB_CALL_DISABLED_MESSAGE);
            navigate("/society/mobile");
          } else if (route) navigate(route);
        }));
        try {
          const { LocalNotifications } = await import("@capacitor/local-notifications");
          listeners.push(await LocalNotifications.addListener("localNotificationActionPerformed", (event) => {
            const route = event?.notification?.extra?.route;
            if (route?.includes("/society/guard-call/") || route?.includes("/society/resident-call/")) {
              toast.info(WEB_CALL_DISABLED_MESSAGE);
              navigate("/society/mobile");
            } else if (route) navigate(route);
          }));
        } catch {}
        if (!cancelled) await PushNotifications.register();
      } catch {
        // Web browser testing and APKs without Firebase config should continue normally.
      }
    };
    setupPush();
    return () => {
      cancelled = true;
      listeners.forEach((listener) => {
        try { listener.remove?.(); } catch {}
      });
    };
  }, [navigate, user?.id, user?.role]);

  useEffect(() => {
    // Native app: the call is already ringing via the native flow (which is
    // what triggered this poll to find it in the first place) - don't also
    // fire this web-side ringtone/toast/notification/full-screen popup for
    // the same call.
    if (isNativeApp) return undefined;
    if (incomingCalls.length === 0) return undefined;
    try { window.RpSocietyAndroid?.startCallRingtone?.(); } catch {}
    const leadCall = incomingCalls[0];
    // Tell the caller's screen this phone is actually ringing (WhatsApp-style
    // "Calling..." -> "Ringing..."). Native Android does this too when the
    // background call service shows the lock-screen UI; this covers the
    // foreground/browser path so it isn't only reliant on the push arriving.
    if (leadCall?.id) {
      api.post(`/public/society/calls/${leadCall.id}/ring-ack`, null, { params: { role: "society_member" } }).catch(() => {});
    }
    if (leadCall?.id && lastIncomingCallToastRef.current !== leadCall.id) {
      lastIncomingCallToastRef.current = leadCall.id;
      toast.info(`${leadCall.watchmanName || "Guard"} is calling`, { duration: 8000 });
    }
    if (leadCall?.id && lastLocalCallNoticeRef.current !== leadCall.id) {
      lastLocalCallNoticeRef.current = leadCall.id;
      import("@capacitor/local-notifications").then(async ({ LocalNotifications }) => {
        const permission = await LocalNotifications.requestPermissions();
        if (permission.display !== "granted") return;
        await LocalNotifications.schedule({
          notifications: [{
            id: Math.abs(Array.from(String(leadCall.id)).reduce((sum, char) => sum + char.charCodeAt(0), 0)) % 2147483647,
            title: "Incoming society video call",
            body: `${leadCall.watchmanName || "Guard"} is calling your flat`,
            sound: "default",
            extra: { route: "/society/mobile" },
          }],
        });
      }).catch(() => {});
    }
    let audioContext;
    let stopped = false;
    const ringOnce = () => {
      try {
        navigator.vibrate?.([450, 200, 450]);
      } catch {}
      try {
        audioContext = audioContext || new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gain = audioContext.createGain();
        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
        gain.gain.setValueAtTime(0.0001, audioContext.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.2, audioContext.currentTime + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.55);
        oscillator.connect(gain);
        gain.connect(audioContext.destination);
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.6);
      } catch {}
    };
    ringOnce();
    const timer = window.setInterval(() => {
      if (!stopped) ringOnce();
    }, 1800);
    return () => {
      try { window.RpSocietyAndroid?.stopCallRingtone?.(); } catch {}
      stopped = true;
      window.clearInterval(timer);
      try { navigator.vibrate?.(0); } catch {}
      try { audioContext?.close?.(); } catch {}
    };
  }, [incomingCalls]);

  const signOut = async () => {
    await logout();
    window.location.href = "/login";
  };

  const openCallRingtoneSettings = () => {
    try {
      window.RpSocietyAndroid?.openCallRingtoneSettings?.();
      toast.info("Open Incoming RP Society Calls and choose Sound / Ringtone.");
    } catch {
      toast.info("Open Android app notification settings and choose Incoming RP Society Calls sound.");
    }
  };

  // Two Android permissions commonly block reliable background call alerts on
  // real devices: (1) the full-screen incoming-call UI on Android 14+, which
  // is off by default and falls back to a plain notification until granted,
  // and (2) battery optimization, which some phone brands use to kill the
  // app's background process a short time after it's closed. Both have to be
  // granted manually by the resident - this just jumps straight to the right
  // system screens instead of leaving them to hunt for it.
  const fixBackgroundCallAlerts = () => {
    try {
      window.RpSocietyAndroid?.requestIgnoreBatteryOptimizations?.();
      window.RpSocietyAndroid?.openFullScreenIntentSettings?.();
      toast.info("Allow the battery prompt, then enable Full screen notifications for RP Society.");
    } catch {
      toast.info("Open Android Settings > Apps > RP Society and allow full-screen notifications plus unrestricted battery usage.");
    }
  };

  const actOnVisitor = async (visitor, action) => {
    try {
      setBusy(true);
      await api.patch(`/society/mobile/visitors/${visitor.id}/${action}`, { remarks: "" });
      toast.success(action === "approve" ? "Visitor approved" : "Visitor rejected");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update visitor"));
    } finally {
      setBusy(false);
    }
  };

  const openVisitorCall = async (visitor) => {
    try {
      const res = await api.get(`/society/visitors/${visitor.id}/call/latest`);
      const callId = res.data?.id;
      if (!callId) {
        toast.error("No active video call found");
        return;
      }
      toast.info(WEB_CALL_DISABLED_MESSAGE);
    } catch (err) {
      toast.error(formatErr(err, "No active video call found"));
    }
  };

  const startStaffCall = async (staff) => {
    toast.info(WEB_CALL_DISABLED_MESSAGE);
  };

  const openActiveCall = (call) => {
    try { window.RpSocietyAndroid?.stopCallRingtone?.(); } catch {}
    toast.info(WEB_CALL_DISABLED_MESSAGE);
  };

  const declineActiveCall = async (call) => {
    if (!call?.id) return;
    try {
      try { window.RpSocietyAndroid?.stopCallRingtone?.(); } catch {}
      await api.post(`/society/calls/${call.id}/end`, { status: "missed" });
      toast.success("Call declined");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not decline call"));
    }
  };

  const submitPayment = async () => {
    if (!payment.billId || !payment.amount || !payment.utr.trim()) {
      toast.error("Bill, amount and UTR are required");
      return;
    }
    try {
      setBusy(true);
      await api.post(`/society/mobile/bills/${payment.billId}/payments`, {
        amount: Number(payment.amount || 0),
        method: "upi",
        utr: payment.utr,
        remarks: payment.remarks,
      });
      toast.success("Payment submitted for verification");
      setPayment({ billId: "", amount: "", utr: "", remarks: "" });
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not submit payment"));
    } finally {
      setBusy(false);
    }
  };

  const saveContactPreference = async (preferredVisitorMobile) => {
    try {
      setSavingContact(true);
      await api.patch("/society/mobile/owner-contact-preference", { preferredVisitorMobile });
      toast.success("Visitor contact preference saved");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not save contact preference"));
    } finally {
      setSavingContact(false);
    }
  };

  const muteThisDevice = async (minutes) => {
    if (!deviceTokenRef.current) {
      toast.error("This device isn't registered for calls yet - open the app once with notifications allowed, then try again.");
      return;
    }
    try {
      setMutingCalls(true);
      const res = await api.post("/society/mobile/push-token/mute", { token: deviceTokenRef.current, minutes });
      setMutedUntil(res.data?.mutedUntil || "");
      toast.success(minutes ? "Calls muted on this device. Your other devices (if any) will still ring." : "Calls unmuted on this device.");
    } catch (err) {
      toast.error(formatErr(err, "Could not update call mute setting"));
    } finally {
      setMutingCalls(false);
    }
  };

  const markNotificationRead = async (item) => {
    try {
      await api.patch(`/society/mobile/notifications/${item.id}/read`);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update notification"));
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 pb-24 text-slate-950">
      <IncomingCallScreen
        call={isNativeApp ? null : incomingCalls[0]}
        owner={data?.owner}
        onAnswer={() => openActiveCall(incomingCalls[0])}
        onDecline={() => declineActiveCall(incomingCalls[0])}
      />
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3">
          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">RP Society</div>
            <h1 className="text-lg font-black leading-tight">{data?.client?.name || user?.client_name || "Society app"}</h1>
            <div className="mt-1 text-xs text-slate-500">{data?.owner ? `${data.owner.wing} ${data.owner.flatNumber} | ${data.owner.ownerName}` : user?.societyRole || user?.role}</div>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="icon" onClick={openCallRingtoneSettings} aria-label="Call ringtone"><Bell className="h-4 w-4" /></Button>
            <Button type="button" variant="outline" size="icon" onClick={load} aria-label="Refresh"><RefreshCw className="h-4 w-4" /></Button>
            <Button type="button" variant="outline" size="icon" onClick={signOut} aria-label="Logout"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-4 px-4 py-4">
        <div className="grid grid-cols-4 gap-2">
          {["home", "bills", "visitors", "calls"].map((item) => (
            <button key={item} type="button" onClick={() => setTab(item)} className={`h-11 rounded-lg border text-sm font-black capitalize ${tab === item ? "border-slate-950 bg-slate-950 text-white" : "border-slate-200 bg-white"}`}>
              {item}
            </button>
          ))}
        </div>

        {tab === "home" && (
          <>
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl bg-white p-3 text-center shadow-sm"><div className="font-mono text-xl font-black">{data?.counts?.pendingVisitors || 0}</div><div className="text-xs text-slate-500">Visitors</div></div>
              <div className="rounded-xl bg-white p-3 text-center shadow-sm"><div className="font-mono text-xl font-black">{data?.counts?.openBills || 0}</div><div className="text-xs text-slate-500">Bills</div></div>
              <div className="rounded-xl bg-white p-3 text-center shadow-sm"><div className="font-mono text-xl font-black">{data?.counts?.unreadNotifications || 0}</div><div className="text-xs text-slate-500">Alerts</div></div>
            </div>

            {permissions.has("visitors.manage") && <Link to="/society/guard" className="flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-3 text-sm font-black text-white"><Shield className="h-4 w-4" /> Open guard desk</Link>}
            {data?.role === "society_member" && <button type="button" onClick={() => setTab("calls")} className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-700 px-4 py-3 text-sm font-black text-white"><PhoneCall className="h-4 w-4" /> Call guard / staff</button>}
            {data?.role === "society_member" && <button type="button" onClick={openCallRingtoneSettings} className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-950"><Bell className="h-4 w-4" /> Call ringtone</button>}
            {data?.role === "society_member" && (
              <button type="button" onClick={fixBackgroundCallAlerts} className="flex w-full items-center justify-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-black text-amber-900">
                <Bell className="h-4 w-4" /> Fix missed calls when phone is locked
              </button>
            )}

            {data?.role === "society_member" && data?.owner && (
              <Section title="Visitor Contact" icon={Shield}>
                <div className="space-y-3">
                  <div className="rounded-lg bg-slate-50 p-3 text-sm">
                    <div className="font-bold text-slate-950">Choose which number guard should try first.</div>
                    <div className="mt-1 text-slate-500">If the selected number does not respond in time, society staff can use the fallback number.</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button type="button" disabled={savingContact} variant={data.owner.preferredVisitorMobile === "primary" ? "default" : "outline"} onClick={() => saveContactPreference("primary")}>
                      Primary
                    </Button>
                    <Button type="button" disabled={savingContact} variant={data.owner.preferredVisitorMobile === "secondary" ? "default" : "outline"} onClick={() => saveContactPreference("secondary")}>
                      Secondary
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                    <div className="rounded-lg border border-slate-200 p-2"><div className="font-bold text-slate-700">Primary</div>{data.owner.ownerMobile || "Not saved"}</div>
                    <div className="rounded-lg border border-slate-200 p-2"><div className="font-bold text-slate-700">Secondary</div>{data.owner.ownerSecondaryMobile || data.owner.tenantMobile || data.owner.emergencyContact || "Not saved"}</div>
                  </div>
                </div>
              </Section>
            )}

            {data?.role === "society_member" && (
              <Section title="This Device's Calls" icon={PhoneOff}>
                <div className="space-y-3">
                  <div className="rounded-lg bg-slate-50 p-3 text-sm">
                    <div className="font-bold text-slate-950">Going out? Mute calls on just this phone.</div>
                    <div className="mt-1 text-slate-500">
                      If you're logged in on more than one phone, the others keep ringing normally. Guard sees an immediate notice only if ALL of your phones are muted at once - it always auto-unmutes on its own, so you can't forget it's on.
                    </div>
                    {mutedUntil && new Date(mutedUntil) > new Date() && (
                      <div className="mt-2 font-bold text-amber-700">Muted on this device until {new Date(mutedUntil).toLocaleString()}.</div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button type="button" variant="outline" disabled={mutingCalls} onClick={() => muteThisDevice(120)}>Mute 2 hours</Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={mutingCalls}
                      onClick={() => {
                        const target = new Date();
                        target.setDate(target.getDate() + 1);
                        target.setHours(9, 0, 0, 0);
                        muteThisDevice(Math.max(1, Math.round((target - new Date()) / 60000)));
                      }}
                    >
                      Mute until tomorrow 9am
                    </Button>
                  </div>
                  {mutedUntil && new Date(mutedUntil) > new Date() && (
                    <Button type="button" disabled={mutingCalls} className="w-full" onClick={() => muteThisDevice(0)}>Unmute now</Button>
                  )}
                </div>
              </Section>
            )}

            <Section title="Alerts" icon={Bell}>
              <div className="space-y-2">
                {notifications.slice(0, 6).map((item) => {
                  const unread = !(item.readBy || []).includes(user?.id);
                  return (
                    <button key={item.id} type="button" onClick={() => unread && markNotificationRead(item)} className={`w-full rounded-lg p-3 text-left ${unread ? "bg-amber-50" : "bg-slate-50"}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-bold text-slate-950">{item.title || "Alert"}</div>
                          <div className="mt-1 text-sm text-slate-600">{item.message || "-"}</div>
                          <div className="mt-1 text-xs text-slate-400">{item.createdAt ? new Date(item.createdAt).toLocaleString() : "-"}</div>
                        </div>
                        {unread && <Pill tone="amber">New</Pill>}
                      </div>
                    </button>
                  );
                })}
                {notifications.length === 0 && <div className="py-4 text-center text-sm text-slate-500">No alerts yet.</div>}
              </div>
            </Section>

            <Section title="Notices" icon={Bell}>
              <div className="space-y-2">
                {(data?.notices || []).slice(0, 5).map((notice) => (
                  <div key={notice.id} className="rounded-lg bg-slate-50 p-3">
                    <div className="font-bold">{notice.title}</div>
                    <div className="mt-1 line-clamp-2 text-sm text-slate-600">{notice.body || "-"}</div>
                  </div>
                ))}
                {(!data?.notices || data.notices.length === 0) && <div className="py-4 text-center text-sm text-slate-500">No notices yet.</div>}
              </div>
            </Section>
          </>
        )}

        {tab === "bills" && (
          <Section title="Bills and Payment" icon={FileText}>
            <div className="space-y-3">
              {bills.map((bill) => (
                <div key={bill.id} className="rounded-lg border border-slate-200 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div><div className="font-black">{bill.billNumber}</div><div className="text-sm text-slate-500">{bill.billingMonth} | Due {bill.dueDate || "-"}</div></div>
                    <Pill tone={Number(bill.balanceDue || 0) > 0 ? "amber" : "green"}>{bill.status}</Pill>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                    <div><div className="text-slate-500">Total</div><div className="font-bold">₹ {Number(bill.totalDue || 0).toLocaleString()}</div></div>
                    <div><div className="text-slate-500">Paid</div><div className="font-bold">₹ {Number(bill.paidAmount || 0).toLocaleString()}</div></div>
                    <div><div className="text-slate-500">Balance</div><div className="font-bold">₹ {Number(bill.balanceDue || 0).toLocaleString()}</div></div>
                  </div>
                  {data?.role === "society_member" && Number(bill.balanceDue || 0) > 0 && <Button type="button" variant="outline" className="mt-3 w-full" onClick={() => { setPayment((p) => ({ ...p, billId: bill.id, amount: String(bill.balanceDue || "") })); setTab("bills"); }}><CreditCard className="h-4 w-4" /> Pay / Enter UTR</Button>}
                </div>
              ))}
              {bills.length === 0 && <div className="py-4 text-center text-sm text-slate-500">No bills available.</div>}
              {data?.role === "society_member" && openBills.length > 0 && (
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="mb-2 text-sm font-black">Submit payment UTR</div>
                  <select className="mb-2 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={payment.billId} onChange={(e) => {
                    const bill = bills.find((item) => item.id === e.target.value);
                    setPayment((p) => ({ ...p, billId: e.target.value, amount: bill ? String(bill.balanceDue || "") : p.amount }));
                  }}>
                    <option value="">Select bill</option>
                    {openBills.map((bill) => <option key={bill.id} value={bill.id}>{bill.billNumber} - ₹ {Number(bill.balanceDue || 0).toLocaleString()}</option>)}
                  </select>
                  <Input className="mb-2" type="number" placeholder="Amount" value={payment.amount} onChange={(e) => setPayment((p) => ({ ...p, amount: e.target.value }))} />
                  <Input className="mb-2" placeholder="UTR number" value={payment.utr} onChange={(e) => setPayment((p) => ({ ...p, utr: e.target.value }))} />
                  <Textarea className="mb-2" rows={2} placeholder="Remarks" value={payment.remarks} onChange={(e) => setPayment((p) => ({ ...p, remarks: e.target.value }))} />
                  <Button disabled={busy || !selectedBill} className="w-full bg-slate-950 text-white hover:bg-slate-800" onClick={submitPayment}>Submit for verification</Button>
                </div>
              )}
            </div>
          </Section>
        )}

        {tab === "visitors" && (
          <Section title="Visitors" icon={DoorOpen}>
            <div className="space-y-3">
              {visitors.map((visitor) => (
                <div key={visitor.id} className="rounded-lg border border-slate-200 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div><div className="font-black">{visitor.visitorName || "Visitor"}</div><div className="text-sm text-slate-500">{visitor.purpose || "Visit"} | {visitor.inTime ? new Date(visitor.inTime).toLocaleString() : "-"}</div></div>
                    <Pill tone={visitor.status === "approved" ? "green" : visitor.status === "rejected" ? "red" : "amber"}>{visitor.status}</Pill>
                  </div>
                  {visitor.status === "pending" && (data?.role === "society_member" || permissions.has("visitors.manage")) && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {data?.role === "society_member" && <Button className="col-span-2 bg-slate-950 text-white hover:bg-slate-800" onClick={() => openVisitorCall(visitor)}>Open video call</Button>}
                      <Button disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => actOnVisitor(visitor, "approve")}><Check className="h-4 w-4" /> Approve</Button>
                      <Button disabled={busy} variant="outline" onClick={() => actOnVisitor(visitor, "reject")}><X className="h-4 w-4" /> Reject</Button>
                    </div>
                  )}
                </div>
              ))}
              {visitors.length === 0 && <div className="py-4 text-center text-sm text-slate-500">No visitors available.</div>}
              {pendingVisitors.length > 0 && <div className="text-center text-xs text-slate-500">{pendingVisitors.length} pending approval</div>}
            </div>
          </Section>
        )}

        {tab === "calls" && (
          <Section title="Video Calls" icon={PhoneCall}>
            {data?.role !== "society_member" ? (
              <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-600">Staff can receive resident calls from the guard desk.</div>
            ) : (
              <div className="space-y-3">
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <div className="font-black text-slate-950">Call guard or society staff</div>
                  <div className="mt-1 text-sm text-slate-600">
                    Tap a name to start a video call. Committee member calling is hidden for privacy.
                  </div>
                  <Button type="button" variant="outline" className="mt-3 w-full bg-white" onClick={openCallRingtoneSettings}>
                    <Bell className="h-4 w-4" /> Change call ringtone
                  </Button>
                </div>
                {activeCalls.length > 0 && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <div className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Active calls</div>
                    <div className="mt-2 grid gap-2">
                      {activeCalls.map((call) => (
                        <Button key={call.id} type="button" className="justify-start bg-blue-700 text-white hover:bg-blue-800" onClick={() => openActiveCall(call)}>
                          <PhoneCall className="h-4 w-4" /> {call.initiatorRole === "watchman" ? `Answer ${call.watchmanName || "guard"} call` : `Rejoin call with ${call.targetStaffName || "staff"}`}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid gap-2">
                  {callContacts.map((staff) => (
                    <Button key={staff.id} type="button" disabled={busy} variant="outline" className="h-auto justify-start p-3 text-left" onClick={() => startStaffCall(staff)}>
                      <PhoneCall className="h-4 w-4" />
                      <span>
                        <span className="block font-black">{staff.name || "Staff"}</span>
                        <span className="block text-xs font-normal text-slate-500">{staff.societyRole === "security" ? "Guard / Security" : staff.societyRole || "Staff"} {staff.phone ? `- ${staff.phone}` : ""}</span>
                      </span>
                    </Button>
                  ))}
                  {callContacts.length === 0 && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                      No guard or staff login is available for calling. Create a Watchman / Staff app login in the society admin panel, then refresh this app.
                    </div>
                  )}
                </div>
              </div>
            )}
          </Section>
        )}
      </main>
    </div>
  );
}
